import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Chip,
  Grid,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { feeMonitorSeverityToLabel } from "../../utils/feeMonitorUtils";
import DataGridExport from "../../components/DataGridExport";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const formatCurrency0 = (value) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  return number.toLocaleString("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
};

const formatCurrency2 = (value) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  return number.toLocaleString("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "-";
  const number = typeof value === "number" ? value : Number(value);
  return `${(number * 100).toFixed(1)}%`;
};

const formatSignedCurrency0 = (value) => {
  const number = typeof value === "number" ? value : Number(value ?? 0);
  const sign = number > 0 ? "+" : "";
  return `${sign}${formatCurrency0(number)}`;
};

const severityDotSx = (severity) => {
  const severityLabel = feeMonitorSeverityToLabel(severity);
  const paletteKey =
    severityLabel === "RED"
      ? "error.main"
      : severityLabel === "AMBER"
      ? "warning.main"
      : severityLabel === "INFO"
      ? "info.main"
      : "grey.500";
  return {
    width: 12,
    height: 12,
    borderRadius: "50%",
    bgcolor: paletteKey,
    display: "inline-block",
  };
};

const FeeMonitorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rows, setRows] = useState([]);
  const missingAgreementCustomerCount = useMemo(
    () => rows.filter((row) => row?.hasMissingPricingAgreement).length,
    [rows]
  );

  const defaultSortModel = useMemo(
    () => [
      {
        field: "underchargeYearUi",
        sort: "desc",
      },
    ],
    []
  );

  const fetchRows = useCallback(async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const response = await axios.get("/api/v1/feemonitor/dashboard", {
        params: { page: 1, pageSize: 1000, sort: "overchargeyear_desc" },
      });
      const data = response.data || {};
      setRows(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      setErrorMessage(error?.response?.data?.detail || error?.message || "Error fetching fee monitor dashboard");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleOpenCustomer = useCallback(
    (row) => {
      if (!row?.customerId) return;
      navigate(`/customers/customeroverview?customerId=${row.customerId}&customerNumber=${row.customerNumber}&tab=11`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "pricingStatus",
        headerName: "Monitor",
        width: 90,
        sortable: true,
        renderCell: (params) => (
          <Tooltip title={feeMonitorSeverityToLabel(params.value)}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
              <Box sx={severityDotSx(params.value)} />
            </Box>
          </Tooltip>
        ),
      },
      { field: "customerNumber", headerName: "Customer Number", width: 140 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 220 },
      {
        field: "underchargeYearUi",
        headerName: "Undercharge/year",
        width: 160,
        align: "right",
        headerAlign: "right",
        // Backend: overchargeYear is currently computed as sum(max(0, deltaFeeMonthly) * 12)
        // deltaFeeMonthly = actualFee - baselineFee
        // => positive delta means the customer should be paying more (we are undercharging)
        valueGetter: (params) => params.row?.overchargeYear ?? 0,
        renderCell: (params) => (
          <Box sx={{ width: "100%", textAlign: "right", pr: 1 }}>{formatCurrency0(params.value)}</Box>
        ),
      },
      {
        field: "overchargeYearUi",
        headerName: "Overcharge/year",
        width: 160,
        align: "right",
        headerAlign: "right",
        // Backend: underchargeYear is currently computed as sum(max(0, -deltaFeeMonthly) * 12)
        // => negative delta means the customer should be paying less (we are overcharging)
        valueGetter: (params) => params.row?.underchargeYear ?? 0,
        renderCell: (params) => (
          <Box sx={{ width: "100%", textAlign: "right", pr: 1 }}>{formatCurrency0(params.value)}</Box>
        ),
      },
      {
        field: "netDeltaYear",
        headerName: "Net difference/year",
        width: 130,
        align: "right",
        headerAlign: "right",
        valueGetter: (params) => params.row?.netDeltaYear ?? 0,
        renderCell: (params) => {
          const deltaYear = Number(params.value ?? 0);
          const color =
            deltaYear < 0
              ? "error.main" // Customer pays too little
              : deltaYear > 0
              ? "warning.main" // Customer pays too much
              : "success.main"; // Customer pays as expected
          return (
            <Box sx={{ width: "100%", textAlign: "right", pr: 1, color, fontWeight: 600 }}>
              {formatSignedCurrency0(deltaYear)}
            </Box>
          );
        },
      },
      { field: "redItemCount", headerName: "# red", width: 90, align: "right", headerAlign: "right" },
      {
        field: "topIssue",
        headerName: "Top issue",
        flex: 1,
        minWidth: 220,
        valueGetter: (params) => {
          const issue = params.row?.topIssue;
          if (!issue) return "-";
          const delta = issue.deltaFeeMonthly ?? 0;
          const sign = delta >= 0 ? "+" : "";
          return `${issue.countryCode} VAT (${sign}${formatCurrency2(delta)}/m)`;
        },
      },
      {
        field: "missingPricingAgreementCount",
        headerName: "Missing agreements",
        width: 170,
        align: "center",
        headerAlign: "center",
        valueGetter: (params) => Number(params.row?.missingPricingAgreementCount ?? 0),
        renderCell: (params) => {
          const count = Number(params.value ?? 0);
          if (count <= 0) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
          }

          return <Chip size="small" color="error" variant="outlined" label={String(count)} />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Open customer > Pricing Agreements">
            <IconButton onClick={() => handleOpenCustomer(params.row)} size="small">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleOpenCustomer]
  );

  return (
    <React.Fragment>
      <Helmet title="Customer Fee Monitor (VAT)" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Customer Fee Monitor
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/">
              Pages
            </Link>
            <Typography>Customer Fee Monitor</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Card mb={6}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 5,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Customer Fee Monitor (VAT)
          </Typography>
          <Typography variant="body2" gutterBottom>
            Overview of pricing deviations grouped per customer. Click the eye icon to open the customer’s Pricing Agreements.
          </Typography>
          {missingAgreementCustomerCount > 0 ? (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${missingAgreementCustomerCount} customers with missing pricing agreements`}
              sx={{ mt: 1 }}
            />
          ) : null}
        </CardContent>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Box sx={{ marginBottom: "10px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
              <Box sx={{ display: "flex", gap: 0 }}>
                <DataGridExport tableData={rows} columns={columns} />
              </Box>
            </Box>

            {errorMessage ? <Typography color="error">{errorMessage}</Typography> : null}

            <DataGrid
              getRowId={(row) => row.customerId}
              rows={rows}
              columns={columns}
              getRowClassName={(params) => (params.row?.hasMissingPricingAgreement ? "missing-pricing-agreement-row" : "")}
              density="compact"
              disableColumnSelector
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 250 } },
              }}
              loading={loading}
              pageSize={15}
              sortModel={defaultSortModel}
              sx={{
                height: 667,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": { color: "primary.main" },
                "& .missing-pricing-agreement-row": {
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                },
                "& .missing-pricing-agreement-row:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.14)",
                },
                "& .missing-pricing-agreement-row .MuiDataGrid-cell": {
                  borderColor: "rgba(211, 47, 47, 0.18)",
                },
                marginTop: 0,
              }}
            />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={18} />
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default FeeMonitorDashboard;


